// Editor controlado de "tarifas por comuna" con precio ABSOLUTO. Comparten esta UX la
// tarifa base del operador (CommercePricingSection) y el override por contrato
// (RateCardEditor). Ambos escriben el mismo shape:
//   { communeNames: [comuna, …], baseCost }
//
// Es el hermano de ComunaZonesEditor, y las dos diferencias con él son deliberadas:
//
//   1. PRECIO ABSOLUTO, no un ajuste ±. El cobro al comercio siempre modeló la tarifa de
//      comuna como un precio cerrado; mezclar los dos modelos entre el nivel operador y
//      el del contrato haría que el mismo número signifique cosas distintas según el
//      nivel (ver el docblock de commerce-pricing-config.schema.ts).
//   2. VARIAS comunas por regla, no una. Es la forma que ya tienen los datos guardados
//      (`communeNames` es un array desde siempre) y agrupar comunas al mismo precio es el
//      uso normal. Los chips acumulan de varias regiones: se elige una región, se suman
//      sus comunas, se cambia de región y se siguen sumando.
//
// Lo que sí se comparte con ComunaZonesEditor es el principio de NO PERDER TARIFAS: una
// comuna guardada que hoy no es seleccionable (quedó fuera de cobertura, o se cargó con
// un typo cuando el campo era texto libre) se muestra marcada pero sigue viajando en el
// payload. Ocultarla la borraría en silencio, y son montos que se facturan.
import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { AlertTriangle, Globe, Plus, Trash2, X } from "lucide-react";
import { Button } from "../../../../components/ui/Button";
import SearchDropdown from "../../../../components/searchDropdown";
import useCommuneCatalog, {
  normalizeCommuneName,
  titleCaseCommune,
} from "../../../../hooks/useCommuneCatalog";
import { useCountry } from "../../../../context/CountryContext";
import { pluralize } from "../../../../utils/divisionLabels";

const inputCls =
  "h-9 rounded-md border border-border bg-bg px-2 text-sm w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500";

// Ruta de la pestaña donde el operador declara dónde opera.
const COVERAGE_TAB_PATH = "/admin/settings?tab=coverage";

let _uidSeq = 0;
const uid = () => `cr${++_uidSeq}`;

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * API -> filas de UI. Una fila por regla, conservando el agrupamiento de comunas tal cual
 * está guardado. `regionId` nace vacío y NO se hidrata: en este editor es estado efímero
 * ("qué región estoy mirando para agregar"), no un dato de la regla — una misma regla
 * puede tener comunas de varias regiones.
 */
export const communeRatesFromApi = (apiZones) =>
  (Array.isArray(apiZones) ? apiZones : []).map((z) => ({
    id: uid(),
    regionId: "",
    communes: (Array.isArray(z?.communeNames) ? z.communeNames : []).filter(Boolean),
    baseCost: z?.baseCost != null ? String(z.baseCost) : "",
  }));

/**
 * Filas de UI -> API. Descarta las filas sin ninguna comuna, exactamente igual que el
 * editor de texto libre al que reemplaza.
 *
 * Un `baseCost` vacío viaja como 0 (= envío gratis en esas comunas), que es el
 * comportamiento que ya tenía el campo de texto. No se descarta ni se rechaza acá: el
 * backend acepta 0 (`@Min(0)`) y cambiarlo alteraría tarifas ya guardadas.
 */
export const communeRatesToApi = (rows) =>
  (Array.isArray(rows) ? rows : [])
    .map((r) => ({
      communeNames: (Array.isArray(r?.communes) ? r.communes : [])
        .map((c) => String(c ?? "").trim())
        .filter(Boolean),
      baseCost: Math.round(num(r?.baseCost)),
    }))
    .filter((z) => z.communeNames.length > 0);

/**
 * Filas que `communeRatesToApi` descartaría en silencio (sin ninguna comuna). Alimenta el
 * aviso previo al guardado: para el operador, una fila descartada sin decir nada es
 * indistinguible de una tarifa guardada.
 */
export const countIncompleteCommuneRateRows = (rows) =>
  (Array.isArray(rows) ? rows : []).filter(
    (r) =>
      (Array.isArray(r?.communes) ? r.communes : []).filter((c) =>
        String(c ?? "").trim(),
      ).length === 0,
  ).length;

export default function CommuneRatesEditor({
  rows,
  onChange,
  currency = "CLP",
  // Cobertura declarada del operador, tal cual viene en `logisticscompanies.coverageAreas`.
  // Ver el contrato de los 3 estados (array / [] / null) en useCommuneCatalog.
  coverageAreas = null,
  coverageLoading = false,
  // ¿Explicar en pantalla por qué se ofrece el país entero? Solo tiene sentido cuando la
  // cobertura que se pasó es la del usuario que está editando. Si el consumidor decidió
  // no filtrar por otro motivo (p. ej. el contrato es de otro operador), el aviso diría
  // "todavía no defines tu cobertura" sobre una cobertura que sí existe.
  showCoverageHint = true,
  title,
  description,
}) {
  const { t } = useTranslation();
  const { labels } = useCountry();
  const regionWord = labels?.region ?? "Región";
  const communeWord = labels?.commune ?? "Comuna";
  const regionLower = regionWord.toLowerCase();
  const communeLower = communeWord.toLowerCase();

  const {
    mode,
    coverageKnown,
    countryName,
    regionOptions,
    communeOptions,
    ensureCommunes,
    communeStatus,
    loadingRegions,
    isOutOfCatalog,
  } = useCommuneCatalog({ coverageAreas, coverageLoading });

  const safeRows = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);

  const addRow = () =>
    onChange([...safeRows, { id: uid(), regionId: "", communes: [], baseCost: "" }]);
  const setRow = (i, patch) =>
    onChange(safeRows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const removeRow = (i) => onChange(safeRows.filter((_, idx) => idx !== i));

  // Comunas ya usadas en las OTRAS filas. El backend del operador rechaza con 400 una
  // comuna repetida entre tarifas (el motor resuelve por primer match, así que dos
  // precios para la misma comuna darían un cobro arbitrario). En vez de dejar que el
  // operador arme ese error y se lo devuelva el guardado, no se le ofrece la opción.
  const namesUsedElsewhere = useCallback(
    (rowIndex) => {
      const set = new Set();
      safeRows.forEach((r, idx) => {
        if (idx === rowIndex) return;
        (Array.isArray(r?.communes) ? r.communes : []).forEach((c) =>
          set.add(normalizeCommuneName(c)),
        );
      });
      return set;
    },
    [safeRows],
  );

  const availableCommunes = useCallback(
    (rowIndex, row) => {
      const taken = namesUsedElsewhere(rowIndex);
      const own = new Set(
        (Array.isArray(row?.communes) ? row.communes : []).map(normalizeCommuneName),
      );
      return communeOptions(row?.regionId).filter((c) => {
        const key = normalizeCommuneName(c.value);
        return !taken.has(key) && !own.has(key);
      });
    },
    [communeOptions, namesUsedElsewhere],
  );

  const onRegionChange = (i, regionId) => {
    setRow(i, { regionId });
    ensureCommunes(regionId);
  };

  const addCommune = (i, name) => {
    if (!name) return;
    const current = Array.isArray(safeRows[i]?.communes) ? safeRows[i].communes : [];
    const key = normalizeCommuneName(name);
    if (current.some((c) => normalizeCommuneName(c) === key)) return;
    setRow(i, { communes: [...current, name] });
  };

  const removeCommune = (i, index) => {
    const current = Array.isArray(safeRows[i]?.communes) ? safeRows[i].communes : [];
    setRow(i, { communes: current.filter((_, idx) => idx !== index) });
  };

  const regionLabel = (regionId) =>
    regionOptions.find((r) => r.value === regionId)?.label || "";

  const communePlaceholder = (row) => {
    if (mode === "loading") return t("Cargando…");
    if (!row.regionId) return t("Primero elige {{region}}", { region: regionLower });
    if (mode === "coverage") {
      const st = communeStatus[row.regionId];
      if (st === "loading") return t("Cargando…");
      if (st === "error") return t("Error al cargar; reintenta");
    }
    return t("Buscar y agregar…");
  };

  // En modo 'country' el aviso no puede hablar de cobertura: ahí no se filtró nada, así
  // que un nombre no seleccionable es un typo o una comuna renombrada en el catálogo.
  const outOfCatalogNotice =
    mode === "coverage"
      ? t(
          "Fuera de tu cobertura actual: estas tarifas se guardan y se siguen cobrando. Para dejar de usarlas, quita la {{commune}}.",
          { commune: communeLower },
        )
      : t(
          "No están en el catálogo de {{country}}: se guardan y se siguen cobrando, pero revisa que el nombre sea correcto.",
          { country: countryName || t("tu país") },
        );

  const rowOutOfCatalog = (row) =>
    (Array.isArray(row?.communes) ? row.communes : []).some((c) => isOutOfCatalog(c));

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-sm font-medium text-fg">
          {title ?? t("Tarifas por {{commune}}", { commune: communeLower })}
        </label>
        <Button
          variant="ghost"
          size="sm"
          iconLeft={<Plus className="w-4 h-4" />}
          onClick={addRow}
          type="button"
        >
          {t("Agregar")}
        </Button>
      </div>
      <p className="text-xs text-fg-muted mb-2">
        {description ??
          t(
            "Precio cerrado para esas {{communes}}, en vez del precio base. Una {{commune}} no puede estar en dos tarifas.",
            { communes: `${communeLower}s`, commune: communeLower },
          )}
      </p>

      {/* Sin cobertura declarada se ofrece el catálogo completo del país, pero se dice
          por qué: acotar los selectores a la cobertura es lo esperable y su ausencia
          tiene arreglo a un clic. */}
      {mode === "country" && coverageKnown && showCoverageHint && (
        <div className="rounded-md border border-border bg-bg-muted p-3 mb-2 flex items-start gap-2">
          <Globe className="w-4 h-4 text-fg-subtle mt-0.5 shrink-0" />
          <div className="text-xs text-fg-muted">
            <p>
              {t(
                "Todavía no defines tu cobertura, así que se ofrecen todas las {{communes}} del país.",
                { communes: `${communeLower}s` },
              )}
            </p>
            <Link
              to={COVERAGE_TAB_PATH}
              className="text-brand-600 hover:underline font-medium"
            >
              {t("Ir a Cobertura")}
            </Link>
          </div>
        </div>
      )}

      {/* Hay cobertura declarada pero ninguna de sus regiones llegó al catálogo: o falló
          la lectura, o la cobertura apunta a ids que ya no existen. En los dos casos el
          selector queda vacío, y a diferencia de otros editores acá "Agregar" sigue
          habilitado, así que sin este aviso el operador vería una fila imposible de
          completar y ninguna explicación. */}
      {mode === "coverage" && !loadingRegions && regionOptions.length === 0 && (
        <p className="flex items-start gap-1.5 text-xs text-status-warning mb-2">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
          <span>
            {t(
              "No se pudo cargar el catálogo de {{regions}} de tu cobertura; recarga la página para volver a intentarlo.",
              { regions: pluralize(regionWord).toLowerCase() },
            )}
          </span>
        </p>
      )}

      {/* Cobertura ilegible: se ofrece el catálogo del país igual (bloquear sería quitar
          una capacidad que el campo de texto libre sí daba), pero no se afirma nada sobre
          cobertura ni se marca ninguna tarifa. */}
      {mode === "country" && !coverageKnown && !coverageLoading && showCoverageHint && (
        <p className="text-xs text-fg-muted mb-2">
          {t(
            "No se pudo leer tu cobertura: se ofrecen todas las {{communes}} del país. Recarga la página para volver a intentarlo.",
            { communes: `${communeLower}s` },
          )}
        </p>
      )}

      {safeRows.length === 0 && (
        <p className="text-xs text-fg-muted">
          {t("Sin tarifas por {{commune}}: todo se cobra al precio base.", {
            commune: communeLower,
          })}
        </p>
      )}

      {safeRows.map((row, i) => {
        const communes = Array.isArray(row.communes) ? row.communes : [];
        const flagged = rowOutOfCatalog(row);
        const options = availableCommunes(i, row);
        return (
          <div
            key={row.id ?? i}
            className={`flex flex-col gap-2 mb-2 p-2 rounded-md border ${
              flagged
                ? "border-status-warning/50 bg-status-warning-soft"
                : "border-border/60"
            }`}
          >
            <div className="flex flex-wrap items-end gap-2">
              {/* Región: acota la lista de comunas. No es parte de la regla, solo el
                  filtro de búsqueda; por eso los chips sobreviven al cambiarla.
                  El `key` la remonta al cambiar de región por el mismo motivo que en el
                  selector de comuna: SearchDropdown deja el texto buscado en estado
                  interno y lo reemplaza por la opción elegida (searchDropdown.jsx:42-46),
                  así que sin esto al reabrirlo la lista queda filtrada por la región ya
                  elegida y no se puede cambiar sin borrar el campo a mano. */}
              <div className="flex flex-col gap-1 min-w-[140px] flex-1">
                <span className="text-xs text-fg-muted">{regionWord}</span>
                <SearchDropdown
                  key={`${row.id ?? i}-region-${row.regionId}`}
                  options={regionOptions}
                  selected={regionLabel(row.regionId)}
                  onChange={(v) => onRegionChange(i, v)}
                  placeholder={
                    loadingRegions || mode === "loading"
                      ? t("Cargando…")
                      : t("Seleccione…")
                  }
                  disabled={loadingRegions || regionOptions.length === 0}
                  ariaLabel={`${t("Seleccionar")} ${regionLower}`}
                />
              </div>

              {/* Comuna: cada selección AGREGA un chip en vez de reemplazar.
                  El `key` incluye la cantidad de comunas para remontar el dropdown tras
                  cada elección: SearchDropdown guarda el texto buscado en estado interno
                  y lo reemplaza por la opción elegida (searchDropdown.jsx:42-46), así que
                  sin esto el siguiente despliegue aparecería filtrado por la comuna que
                  se acaba de agregar. */}
              <div className="flex flex-col gap-1 min-w-[140px] flex-1">
                <span className="text-xs text-fg-muted">{communeWord}</span>
                <SearchDropdown
                  key={`${row.id ?? i}-${row.regionId}-${communes.length}`}
                  options={options}
                  selected=""
                  onChange={(v) => addCommune(i, v)}
                  placeholder={communePlaceholder(row)}
                  disabled={!row.regionId || options.length === 0}
                  ariaLabel={`${t("Agregar")} ${communeLower}`}
                />
              </div>

              <div className="flex flex-col gap-1">
                <span className="text-xs text-fg-muted">
                  {t("Precio")} ({currency})
                </span>
                <input
                  type="number"
                  min="0"
                  className={`${inputCls} max-w-[140px]`}
                  value={row.baseCost}
                  onChange={(e) => setRow(i, { baseCost: e.target.value })}
                  placeholder={t("Ej: 3000")}
                  aria-label={t("Precio")}
                />
              </div>

              <button
                type="button"
                className="h-9 px-2 text-fg-muted hover:text-status-error"
                onClick={() => removeRow(i)}
                aria-label={t("Quitar tarifa")}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>

            {/* Comunas de la regla */}
            {communes.length === 0 ? (
              <p className="text-xs text-fg-muted">
                {t("Sin {{communes}}: esta tarifa se descarta al guardar.", {
                  communes: `${communeLower}s`,
                })}
              </p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {communes.map((name, idx) => {
                  const orphan = isOutOfCatalog(name);
                  return (
                    <span
                      key={`${name}-${idx}`}
                      title={titleCaseCommune(name)}
                      className={`inline-flex items-center gap-1 h-7 pl-2.5 pr-1 rounded-full text-xs font-medium border ${
                        orphan
                          ? "border-status-warning/60 bg-bg text-status-warning"
                          : "border-brand-500 bg-brand-50 text-brand-700"
                      }`}
                    >
                      {orphan && <AlertTriangle className="w-3 h-3 shrink-0" />}
                      {titleCaseCommune(name)}
                      <button
                        type="button"
                        className="h-5 w-5 inline-flex items-center justify-center rounded-full hover:bg-bg-muted"
                        onClick={() => removeCommune(i, idx)}
                        aria-label={`${t("Quitar")} ${titleCaseCommune(name)}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}

            {/* Un precio vacío se guarda como 0 (envío gratis). Es el comportamiento que
                ya tenía el campo de texto; se dice en pantalla para que sea una decisión
                y no un descuido. */}
            {communes.length > 0 && String(row.baseCost ?? "").trim() === "" && (
              <p className="text-[11px] text-fg-muted">
                {t("Sin precio: estas {{communes}} se cobrarán a 0.", {
                  communes: `${communeLower}s`,
                })}
              </p>
            )}

            {flagged && (
              <p className="flex items-start gap-1.5 text-[11px] text-status-warning">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
                <span>{outOfCatalogNotice}</span>
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
